import React, { useState, useRef, ChangeEvent } from "react";
import { Upload, X, Image } from "lucide-react";
import {
  getImageDimensions,
  resizeImage,
  validateImageFile,
} from "@hooks/functions/Image/resize";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";

interface ImageUploadProps {
  onImageChange?: (base64: string | null) => void;
  resizeWidth?: number;
  resizeHeight?: number;
  maxKbSize?: number;
  initialImage?: string | null;
  className?: string;
  convertToWebp?: boolean;
}

export const ImageThumbUploader: React.FC<ImageUploadProps> = ({
  onImageChange,
  resizeWidth,
  resizeHeight,
  maxKbSize = 5120, // Default: 5mb
  initialImage = null,
  className = "",
  convertToWebp = false,
}) => {
  // --- Load some Functions --- //
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Set some Use States --- //
  const [imageBase64, setImageBase64] = useState<string | null>(initialImage);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = async (file: File) => {
    // --- Validate the file --- //
    const validation = validateImageFile(file, maxKbSize);
    if (!validation.valid) {
      return pushToastify({
        message:
          validation.error || "Ocorreu um erro interno na validação da imagem.",
        type: "error",
        theme: "dark",
      });
    }

    setIsProcessing(true);

    try {
      let finalWidth = resizeWidth;
      let finalHeight = resizeHeight;

      // If dimensions not provided, get original image dimensions
      if (!resizeWidth || !resizeHeight) {
        try {
          const dimensions = await getImageDimensions(file);
          finalWidth = dimensions.width;
          finalHeight = dimensions.height;
        } catch (error) {
          console.error("Error getting image dimensions:", error);
          return pushToastify({
            message: `Error getting image dimensions: ${error}`,
            type: "error",
            theme: "dark",
          });
        }
      }

      // Usar a função global de redimensionamento
      const resizedBase64 = await resizeImage(
        file,
        finalWidth,
        finalHeight,
        0.8,
        convertToWebp
      );

      setImageBase64(resizedBase64);
      onImageChange?.(resizedBase64);
    } catch (error) {
      console.error("Erro detalhado ao processar a imagem:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";

      return pushToastify({
        message: `Erro ao processar a imagem: ${errorMessage}\n\nTente com uma imagem diferente.`,
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) await processFile(files[0]);
  };

  const handleRemoveImage = () => {
    setImageBase64(null);
    onImageChange?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    if (!isProcessing) fileInputRef.current?.click();
  };

  return (
    <div className={className}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${
            isDragOver ? "#007bff" : imageBase64 ? "#28a745" : "#dee2e6"
          }`,
          borderRadius: "8px",
          padding: imageBase64 ? "0" : "40px 20px",
          textAlign: "center",
          cursor: isProcessing ? "not-allowed" : "pointer",
          backgroundColor: isDragOver ? "#f8f9fa" : "white",
          transition: "all 0.3s ease",
          position: "relative",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isProcessing ? 0.6 : 1,
        }}
      >
        {imageBase64 ? (
          <div style={{ position: "relative", width: "100%" }}>
            <img
              src={imageBase64}
              alt="Preview"
              style={{
                width: "100%",
                height: "200px",
                objectFit: "contain", // Mudança aqui - mostra a imagem completa sem cortar
                backgroundColor: "#f8f9fa", // Fundo cinza claro para contrastar
                borderRadius: "6px",
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                background: "#dc3545",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                zIndex: 10, // Garantir que fique acima do overlay
              }}
              disabled={isProcessing}
              title="Remover imagem" // Tooltip para deixar claro o que faz
            >
              <X size={16} color="white" />
            </button>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0)",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "all 0.3s ease",
                zIndex: 5, // Menor que o botão de remover
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.6)";
                e.currentTarget.style.opacity = "1";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0,0,0,0)";
                e.currentTarget.style.opacity = "0";
              }}
            >
              <div style={{ color: "white", textAlign: "center" }}>
                <Upload size={24} style={{ marginBottom: "8px" }} />
                <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                  Alterar imagem
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {isProcessing ? (
              <div>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #007bff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 16px",
                  }}
                />
                <div style={{ color: "#6c757d", fontSize: "14px" }}>
                  Processando imagem...
                </div>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    backgroundColor: "#007bff",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <Image size={32} color="white" />
                </div>
                <h5
                  style={{
                    color: "#495057",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  Adicione uma imagem
                </h5>
                <p
                  style={{
                    color: "#6c757d",
                    fontSize: "14px",
                    margin: "0 0 16px",
                  }}
                >
                  Clique aqui ou arraste uma imagem para fazer upload
                </p>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "8px 16px",
                    backgroundColor: "#007bff",
                    color: "white",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#0056b3";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#007bff";
                  }}
                >
                  <Upload size={16} style={{ marginRight: "8px" }} />
                  Escolher arquivo
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {imageBase64 && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "6px",
            fontSize: "13px",
            color: "#155724",
          }}
        >
          ✅ Imagem processada com sucesso!
        </div>
      )}
    </div>
  );
};
